// Copyright (c) 2023 WiseTime. All rights reserved.

import { TilesViewType } from "../action-pane/tiles/tiles"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import * as Actions from "../actions"
import type { RootState } from "../../../app/store"
import { isUSDomain } from "../../../app/config"
import { Translation, TranslationSchema } from "../../../generated/wisetime/user/assistant/v4/assistant_pb"
import { create } from "@bufbuild/protobuf"

export enum TagsViewType {
  NAME = 1,
  DESCRIPTION = 2,
}

export enum TimesheetFieldsViewType {
  LABEL = 1,
  CODE = 2,
}

type PendingNarrative = Translation
type PendingNarrativesCache = {
  [id: string]: PendingNarrative,
}
export type TimeGroupIdsInPendingProgressCache = {
  [postedAt: string]: { timeGroupsIds: string[], postedToSiteId: string },
}

export type TimelineAdditionalUiState = {
  tilesViewType: TilesViewType,
  tagsViewType: TagsViewType,
  timesheetFieldsViewType: TimesheetFieldsViewType,
  autoNarrative: boolean,
  autoNarrativeNeededTimeGroupIds: string[],
  updatingDurationTimeGroupIds: string[],
  pendingNarrativesCache: PendingNarrativesCache,
  timeGroupIdsInPendingProgressCache: TimeGroupIdsInPendingProgressCache,
  blinkTimeRowsColumnTimeGroupIds: string[],
  isPostingTime: boolean,
  warnMultipleTags: boolean,
  postHasDependentProgress: boolean,
  blinkTrashIcon: boolean,
  tileGridIsActive: boolean,
  timesheetScrolledOut: boolean,
  timesheetIsPinned: boolean,
  timesheetIsPinnedByDrag: boolean,
  actionPaneNoteIsHidden: boolean,
  isMergingTimesheets: boolean,

  // To show confetti animation when the user posts a lot of time in one session.
  sessionWithLargeUnpostedTime: boolean,
  showPageConfetti: boolean,
}

export const initialState: TimelineAdditionalUiState = {
  tilesViewType: TilesViewType.GRID,
  tagsViewType: isUSDomain() ? TagsViewType.DESCRIPTION : TagsViewType.NAME,
  timesheetFieldsViewType: TimesheetFieldsViewType.LABEL,
  autoNarrative: true,
  autoNarrativeNeededTimeGroupIds: [],
  updatingDurationTimeGroupIds: [],
  pendingNarrativesCache: {},
  timeGroupIdsInPendingProgressCache: {},
  blinkTimeRowsColumnTimeGroupIds: [],
  isPostingTime: false,
  warnMultipleTags: true,
  postHasDependentProgress: false,
  blinkTrashIcon: false,
  tileGridIsActive: false,
  timesheetScrolledOut: false,
  timesheetIsPinned: false,
  timesheetIsPinnedByDrag: false,
  actionPaneNoteIsHidden: false,
  isMergingTimesheets: false,

  // To show confetti animation when the user posts a lot of time in one session.
  sessionWithLargeUnpostedTime: false,
  showPageConfetti: false,
}

export const slice = createSlice({
  name: "timeline-additional-ui",
  initialState,
  reducers: {
    setTilesViewType: (state, action: PayloadAction<TilesViewType>) => {
      state.tilesViewType = action.payload
    },
    setTagsViewType: (state, action: PayloadAction<TagsViewType>) => {
      state.tagsViewType = action.payload
    },
    setTimesheetFieldsViewType: (state, action: PayloadAction<TimesheetFieldsViewType>) => {
      state.timesheetFieldsViewType = action.payload
    },
    setAutoNarrative: (state, action: PayloadAction<{
      autoNarrative: boolean,
      timeGroupIds?: string[],
    }>) => {
      state.autoNarrative = action.payload.autoNarrative
      if (action.payload.autoNarrative && action.payload.timeGroupIds && action.payload.timeGroupIds.length !== 0) {
        state.autoNarrativeNeededTimeGroupIds.push(...action.payload.timeGroupIds)
      }
    },
    addAutoNarrativeTimeGroupId: (state, action: PayloadAction<string>) => {
      state.autoNarrativeNeededTimeGroupIds.push(action.payload)
    },
    removeAutoNarrativeTimeGroupId: (state, action: PayloadAction<string>) => {
      const index = state.autoNarrativeNeededTimeGroupIds.indexOf(action.payload)
      if (index !== -1) {
        state.autoNarrativeNeededTimeGroupIds.splice(index, 1)
      }
    },
    setPendingNarratives: (state, action: PayloadAction<PendingNarrative[]>) => {
      action.payload.forEach(item => {
        if (state.pendingNarrativesCache[item.key] !== undefined) {
          state.pendingNarrativesCache[item.key] = item
        }
      })
    },
    deletePendingNarratives: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach(id => {
        delete state.pendingNarrativesCache[id]
      })
    },
    addUpdatingDurationTimeGroupId: (state, action: PayloadAction<string>) => {
      state.updatingDurationTimeGroupIds.push(action.payload)
    },
    removeUpdatingDurationTimeGroupId: (state, action: PayloadAction<string>) => {
      const index = state.updatingDurationTimeGroupIds.indexOf(action.payload)
      if (index !== -1) {
        state.updatingDurationTimeGroupIds.splice(index, 1)
      }
    },
    addBlinkTimeRowsColumnTimeGroupIds: (state, action: PayloadAction<string>) => {
      state.blinkTimeRowsColumnTimeGroupIds.push(action.payload)
    },
    removeBlinkTimeRowsColumnTimeGroupIds: (state, action: PayloadAction<string>) => {
      const index = state.blinkTimeRowsColumnTimeGroupIds.indexOf(action.payload)
      if (index !== -1) {
        state.blinkTimeRowsColumnTimeGroupIds.splice(index, 1)
      }
    },
    startPostingTime: (state) => {
      state.isPostingTime = true
    },
    resetIsPostingTime: (state) => {
      state.isPostingTime = false
    },
    setWarnMultipleTags: (state, action: PayloadAction<boolean>) => {
      state.warnMultipleTags = action.payload
    },
    setPostHasDependentProgress: (state) => {
      state.postHasDependentProgress = true
    },
    resetPostHasDependentProgress: (state) => {
      state.postHasDependentProgress = false
    },
    setBlinkTrashIcon: (state, action: PayloadAction<boolean>): void => {
      state.blinkTrashIcon = action.payload
    },
    setTileGridIsActive: (state, action: PayloadAction<boolean>): void => {
      state.tileGridIsActive = action.payload
    },
    setTimesheetScrolledOut: (state, action: PayloadAction<boolean>): void => {
      state.timesheetScrolledOut = action.payload
    },
    setTimesheetIsPinned: (state, action: PayloadAction<boolean>): void => {
      state.timesheetIsPinned = action.payload
    },
    setTimesheetIsPinnedByDrag: (state, action: PayloadAction<boolean>): void => {
      state.timesheetIsPinnedByDrag = action.payload
    },
    setActionPaneNoteIsHidden: (state, action: PayloadAction<boolean>): void => {
      state.actionPaneNoteIsHidden = action.payload
    },
    handlePageConfetti: (state, action: PayloadAction<number>) => {
      const hoursCount = 30 * 3600 // 3600 seconds in 1 hour
      if (!state.sessionWithLargeUnpostedTime && action.payload > hoursCount) {
        state.sessionWithLargeUnpostedTime = true
      }
      if (state.sessionWithLargeUnpostedTime && action.payload === 0) {
        state.showPageConfetti = true
      }
    },
    resetPageConfetti: (state) => {
      state.sessionWithLargeUnpostedTime = false
      state.showPageConfetti = false
    },
    addTimeGroupIdsInPendingProgress: (state, action: PayloadAction<{
      postedAt: string,
      timeGroupsIds: string[],
      postedToSiteId: string,
    }>) => {
      state.timeGroupIdsInPendingProgressCache[action.payload.postedAt] = {
        timeGroupsIds: action.payload.timeGroupsIds,
        postedToSiteId: action.payload.postedToSiteId,
      }
    },
    removeTimeGroupIdsInPendingProgress: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach(postedAt => {
        delete state.timeGroupIdsInPendingProgressCache[postedAt]
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(Actions.postTime, (state) => {
        state.isPostingTime = true
      })
      .addCase(Actions.generateNarrative, (state, action) => {
        const item = create(TranslationSchema, {
          key: action.payload.unpostedTimeGroupId ?? "",
          text: ""
        })
        state.pendingNarrativesCache[item.key] = item
      })
      .addCase(Actions.translateNarratives, (state, action) => {
        action.payload.translations?.forEach(t => {
          const item = create(TranslationSchema, { key: t.key })
          state.pendingNarrativesCache[item.key] = item
        })
      })
      .addCase(Actions.mergeTimeGroups, (state) => {
        state.isMergingTimesheets = true
      })
      .addCase(Actions.mergeTimeGroupsFulfilled, (state) => {
        state.isMergingTimesheets = false
      })
      .addCase(Actions.mergeTimeGroupsErrored, (state) => {
        state.isMergingTimesheets = false
      })
  },
})

export const {
  setTilesViewType,
  setTagsViewType,
  setTimesheetFieldsViewType,
  setAutoNarrative,
  addAutoNarrativeTimeGroupId,
  removeAutoNarrativeTimeGroupId,
  setPendingNarratives,
  deletePendingNarratives,
  addUpdatingDurationTimeGroupId,
  removeUpdatingDurationTimeGroupId,
  addBlinkTimeRowsColumnTimeGroupIds,
  removeBlinkTimeRowsColumnTimeGroupIds,
  startPostingTime,
  resetIsPostingTime,
  setWarnMultipleTags,
  setPostHasDependentProgress,
  resetPostHasDependentProgress,
  setBlinkTrashIcon,
  setTileGridIsActive,
  setTimesheetScrolledOut,
  setTimesheetIsPinned,
  setTimesheetIsPinnedByDrag,
  setActionPaneNoteIsHidden,
  handlePageConfetti,
  resetPageConfetti,
  addTimeGroupIdsInPendingProgress,
  removeTimeGroupIdsInPendingProgress,
} = slice.actions

export const selectTilesViewType = (state: RootState): TilesViewType => state.timelineAdditionalUi.tilesViewType
export const selectTagsViewType = (state: RootState): TagsViewType => state.timelineAdditionalUi.tagsViewType
export const selectTimesheetFieldsViewType = (state: RootState): TimesheetFieldsViewType => state.timelineAdditionalUi.timesheetFieldsViewType
export const selectAutoNarrative = (state: RootState): boolean => state.timelineAdditionalUi.autoNarrative
export const selectAutoNarrativeNeededTimeGroupIds = (state: RootState): string[] =>
  state.timelineAdditionalUi.autoNarrativeNeededTimeGroupIds
export const selectUpdatingDurationTimeGroupIds = (state: RootState): string[] =>
  state.timelineAdditionalUi.updatingDurationTimeGroupIds
export const selectBlinkTimeRowsColumn = () => createSelector(
  (_: RootState, timeGroupId: string) => timeGroupId,
  (state: RootState) => state.timelineAdditionalUi.blinkTimeRowsColumnTimeGroupIds,
  (timeGroupId, blinkTimeRowsColumnTimeGroupIds): boolean => blinkTimeRowsColumnTimeGroupIds.includes(timeGroupId)
)
export const selectPendingNarrativesCache = (state: RootState): PendingNarrativesCache =>
  state.timelineAdditionalUi.pendingNarrativesCache
export const selectPendingNarrative = () => createSelector(
  (_: RootState, timeGroupId: string) => timeGroupId,
  selectPendingNarrativesCache,
  (timeGroupId, pendingNarrativesCache): PendingNarrative | undefined => pendingNarrativesCache[timeGroupId]
)
export const selectIsPostingTime = (state: RootState): boolean => state.timelineAdditionalUi.isPostingTime
export const selectWarnMultipleTags = (state: RootState): boolean => state.timelineAdditionalUi.warnMultipleTags
export const selectPostHasDependentProgress = (state: RootState): boolean => state.timelineAdditionalUi.postHasDependentProgress
export const selectBlinkTrashIcon = (state: RootState): boolean => state.timelineAdditionalUi.blinkTrashIcon
export const selectTileGridIsActive = (state: RootState): boolean => state.timelineAdditionalUi.tileGridIsActive
export const selectTimesheetScrolledOut = (state: RootState): boolean =>
  state.timelineAdditionalUi.timesheetScrolledOut
export const selectTimesheetIsPinned = (state: RootState): boolean =>
  state.timelineAdditionalUi.timesheetIsPinned
  || state.timelineAdditionalUi.timesheetIsPinnedByDrag
export const selectActionPaneNoteIsHidden = (state: RootState): boolean =>
  state.timelineAdditionalUi.actionPaneNoteIsHidden
export const selectShowPageConfetti = (state: RootState): boolean => state.timelineAdditionalUi.showPageConfetti
export const selectIsMergingTimesheets = (state: RootState): boolean => state.timelineAdditionalUi.isMergingTimesheets

export default slice.reducer
